import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Sparkles, AlertTriangle, RefreshCw, Loader2, Target, Layers, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LossWindowEstimatorPanel
 *
 * A live user-facing feature (formerly admin-only). Renders as a
 * collapsible amber-themed section on the Spreadsheet Comparison page.
 * The amber styling signals "cautious/analytical" — the algorithm returns
 * a range with a confidence score, never a definitive event date.
 *
 * Props:
 *   recordId          : string   — the currently-loaded spreadsheet record ID
 *   token             : string   — bearer token
 *   onWindowsForChart : (payload | null) => void
 *                                  Called whenever the user's chart-marker
 *                                  toggle changes state, so the parent can
 *                                  paint / remove ReferenceAreas on the
 *                                  growth chart. Payload shape:
 *                                    { top: window, alternates: window[] }
 *                                  Payload is `null` when the toggle is
 *                                  off or no result is loaded.
 */
export default function LossWindowEstimatorPanel({ recordId, token, onWindowsForChart }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showOnChart, setShowOnChart] = useState(false);

  // Pull previously-persisted estimate for this record so the user sees
  // the last run without re-computing.
  useEffect(() => {
    if (!open || !recordId || result || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/loss-window/${recordId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && r.data) setResult(r.data);
      } catch { /* silent — user can trigger a run */ }
    })();
    return () => { cancelled = true; };
  }, [open, recordId, token]);

  // Reset every piece of state when switching between records. Also
  // notify the parent to strip any chart markers from the previous run.
  useEffect(() => {
    setResult(null);
    setError(null);
    setShowOnChart(false);
    onWindowsForChart?.(null);
  }, [recordId]);

  // Fan-out to the parent whenever the toggle flips OR fresh results arrive.
  useEffect(() => {
    if (!onWindowsForChart) return;
    if (showOnChart && result?.top_window) {
      onWindowsForChart({
        top: result.top_window,
        alternates: (result.top_3_windows || []).slice(1),
      });
    } else {
      onWindowsForChart(null);
    }
  }, [showOnChart, result]);

  const runEstimator = async () => {
    if (!recordId || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/loss-window/estimate/${recordId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      // Automatically light up chart markers on first successful run —
      // this is the most informative default. Users can toggle off.
      if (r.data?.top_window) setShowOnChart(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to run the loss window estimator.');
    } finally {
      setLoading(false);
    }
  };

  const hasResults = !!result?.top_window;

  return (
    <Card
      className="border-2 border-amber-400 bg-amber-50/70 dark:bg-amber-900/10 shadow-sm"
      data-testid="loss-window-panel"
    >
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
        data-testid="loss-window-toggle"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-amber-900 dark:text-amber-200 flex items-center gap-2 flex-wrap">
                Loss Window Estimator
                <Badge className="bg-amber-600 text-white uppercase text-[10px] tracking-wider">
                  Analytical
                </Badge>
              </CardTitle>
              <CardDescription className="text-amber-800/80 dark:text-amber-200/80 mt-1">
                Estimates the most likely period during this cycle when
                unrecorded stock loss may have occurred, based on growth,
                feed, mortality, FCR and model consistency signals. Not a
                confirmed event.
              </CardDescription>
            </div>
          </div>
          <button
            type="button"
            className="text-amber-700 dark:text-amber-300 hover:text-amber-900 p-1"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-4" data-testid="loss-window-body">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={runEstimator}
              disabled={loading || !recordId}
              className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
              data-testid="loss-window-run-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Scoring candidate windows…
                </>
              ) : result ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Re-run estimator
                </>
              ) : (
                <>
                  <Target className="w-4 h-4 mr-1.5" />
                  Estimate loss window
                </>
              )}
            </Button>

            {/* Chart-marker visibility toggle — only meaningful when we
                actually have a top window to plot. */}
            {hasResults && (
              <label
                className="inline-flex items-center gap-2 text-xs font-medium text-amber-900 dark:text-amber-200 bg-white/70 dark:bg-slate-900/40 border border-amber-300 dark:border-amber-700 rounded-full px-3 py-1.5 cursor-pointer select-none hover:bg-white"
                data-testid="loss-window-chart-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={showOnChart}
                  onChange={(e) => setShowOnChart(e.target.checked)}
                  className="accent-amber-600"
                  data-testid="loss-window-chart-toggle"
                />
                {showOnChart ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                <span>Show these windows on the growth chart</span>
              </label>
            )}

            {result?.created_at && (
              <span className="text-[11px] text-amber-800/70 dark:text-amber-300/70">
                Last run: {new Date(result.created_at).toLocaleString()}
              </span>
            )}
          </div>

          {error && (
            <div
              className="text-sm text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-3"
              data-testid="loss-window-error"
            >
              {error}
            </div>
          )}

          {result && result.eligibility_note && !result.top_window && (
            <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-100/60 dark:bg-amber-900/30 rounded p-3">
              <b>No scoreable window:</b> {result.eligibility_note}
            </div>
          )}

          {result?.top_window && (
            <>
              <TopWindowCard top={result.top_window} inputN={result.input_fish_number} />
              <TimelineBar
                cycleStart={result.cycle_start_date}
                cycleEnd={result.cycle_end_date}
                topWindow={result.top_window}
                alternates={result.top_3_windows.slice(1)}
              />
              <TopThreeList windows={result.top_3_windows} />
              <InterpretationBox top={result.top_window} candidates={result.candidates_scored} />
            </>
          )}

          {/* Cautious-language warning — always visible in the panel body */}
          <div
            className="text-[11px] text-amber-900/80 dark:text-amber-200/80 leading-relaxed bg-white/60 dark:bg-slate-900/50 border-l-4 border-amber-500 pl-3 py-2 rounded-r"
            data-testid="loss-window-warning"
          >
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                {result?.warning_text ||
                  'This analysis estimates the most likely period where stock loss may have occurred based on growth, feed, mortality, FCR and model consistency. It does not prove the exact date or cause of loss.'}
              </span>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------------------

function TopWindowCard({ top, inputN }) {
  const start = fmtDate(top.window_start);
  const end = fmtDate(top.window_end);
  const lossPct = inputN ? ((top.estimated_fish_loss / inputN) * 100).toFixed(1) : null;
  return (
    <div
      className="rounded-lg bg-white dark:bg-slate-900 border-2 border-amber-400 p-4 shadow-sm"
      data-testid="loss-window-top"
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-2">
        Most Likely Loss Window
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Window</div>
          <div className="text-base font-bold text-slate-900 dark:text-slate-100">
            {start} → {end}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {top.window_width_days} day window
          </div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Estimated fish loss</div>
          <div className="text-base font-bold text-slate-900 dark:text-slate-100">
            ≈ {top.estimated_fish_loss.toLocaleString()} fish
          </div>
          {lossPct && (
            <div className="text-[11px] text-slate-500 mt-0.5">{lossPct}% of initial stock</div>
          )}
        </div>
        <div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Confidence</div>
          <div className="text-base font-bold text-amber-700 dark:text-amber-300">
            {top.confidence}%
            <span className="ml-2 text-xs font-medium text-amber-600 dark:text-amber-400">
              {top.confidence_label}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {top.confidence >= 75 ? 'Strong signal alignment' : 'Signal alignment: moderate — treat as directional'}
          </div>
        </div>
      </div>
      <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
        <b>Why this window? </b>{top.main_reason}
      </div>
    </div>
  );
}

function TopThreeList({ windows }) {
  if (!windows || windows.length === 0) return null;
  return (
    <div data-testid="loss-window-top3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5">
        <Layers className="w-3.5 h-3.5" />
        Top {windows.length} candidate window{windows.length > 1 ? 's' : ''}, ranked by confidence
      </div>
      <div className="space-y-2">
        {windows.map((w, i) => (
          <div
            key={`${w.window_start}-${w.window_end}`}
            className={`rounded border ${i === 0 ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-900/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'} p-3 text-sm`}
          >
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] font-bold text-slate-400">#{i + 1}</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtDate(w.window_start)} → {fmtDate(w.window_end)}
                </span>
                <span className="text-xs text-slate-500">
                  ({w.window_width_days}d, ≈{w.estimated_fish_loss.toLocaleString()} fish)
                </span>
              </div>
              <Badge
                className={`${w.confidence >= 55 ? 'bg-amber-600' : 'bg-slate-500'} text-white`}
              >
                {w.confidence}% · {w.confidence_label}
              </Badge>
            </div>
            <div className="mt-1.5 text-xs text-slate-600 dark:text-slate-300">
              <b>Main signal:</b> {w.main_reason}
            </div>
            {w.supporting_signals && w.supporting_signals.length > 0 && (
              <ul className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 list-disc pl-4 space-y-0.5">
                {w.supporting_signals.map((s, si) => <li key={si}>{s}</li>)}
              </ul>
            )}
            {w.factors && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(w.factors).map(([name, val]) => (
                  <span
                    key={name}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                    title={`${name} score`}
                  >
                    {name.replace(/_/g, ' ')}: <b>{val}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineBar({ cycleStart, cycleEnd, topWindow, alternates }) {
  if (!cycleStart || !cycleEnd || !topWindow) return null;
  const startMs = new Date(cycleStart).getTime();
  const endMs = new Date(cycleEnd).getTime();
  const total = endMs - startMs;
  if (total <= 0) return null;

  const bar = (winStart, winEnd) => {
    const s = new Date(winStart).getTime();
    const e = new Date(winEnd).getTime();
    return {
      left: `${Math.max(0, ((s - startMs) / total) * 100)}%`,
      width: `${Math.max(0.5, ((e - s) / total) * 100)}%`,
    };
  };

  return (
    <div data-testid="loss-window-timeline">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-1.5">
        Cycle timeline
      </div>
      <div className="relative h-8 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
        {alternates?.map((w) => (
          <div
            key={`alt-${w.window_start}`}
            style={bar(w.window_start, w.window_end)}
            className="absolute top-0 h-full bg-amber-300/60"
            title={`Alt window ${w.confidence}%`}
          />
        ))}
        <div
          style={bar(topWindow.window_start, topWindow.window_end)}
          className="absolute top-0 h-full bg-amber-600 ring-2 ring-amber-800 shadow"
          title={`Top window ${topWindow.confidence}%`}
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1">
        <span>{fmtDate(cycleStart)}</span>
        <span>{fmtDate(cycleEnd)}</span>
      </div>
    </div>
  );
}

function InterpretationBox({ top, candidates }) {
  return (
    <div
      className="text-sm text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded p-3"
      data-testid="loss-window-interpretation"
    >
      <div className="font-semibold mb-1">Interpretation</div>
      Scored {candidates} candidate windows across the cycle. The top window
      returned a <b>{top.confidence_label.toLowerCase()}</b> confidence score
      of <b>{top.confidence}%</b>, driven mainly by:{' '}
      <i>{top.main_reason.toLowerCase()}</i> Use this as a directional pointer
      when reviewing operational records for this period — not as evidence of
      a specific loss date.
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}
